import React, { memo, useContext } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useLocation } from 'react-router-dom';
import { UIDReset } from 'react-uid';
import queryString from 'querystring';
import '@prisjakt/ui-base/main.scss';
import { noop } from '@prisjakt/ui-base';
import { ViewportTracker } from '@prisjakt/ui-viewport';
import ExperimentEditor from '@prisjakt/ui-experiment/src/editor';
import { AppContext } from '@prisjakt/ui-app-context';
import MainHeader from './areas/header';
import Footer from './areas/footer';
import CookieBanner from './components/cookie-banner';
import { CoBrandingBanner } from './components/co-branding-banner';
import CookieBannerSourcePoint from './components/cookie-banner-sourcepoint';
import ErrorMessage from './components/error-message';
import ErrorBoundary from './components/error-boundary';
import ToastContainer from './components/toast-container';
import Sidebar from './areas/sidebar/components/sidebar-desktop';
import Viewport from './components/viewport';
import FetchContext from './components/fetch-context';
import StaticIntercom from './components/static-intercom';
import NewsletterSignupModal, {
  NEWSLETTER_PARAM,
} from './components/newsletter-signup-modal';
import ShutdownMessage from './components/shutdown-message';
import Feedback from './areas/feedback';
import TransitionRouter from './routes';
import { TocModal } from './components/toc-modal';
import addPerformanceTiming from './client/performance-reporter';
import './components/toast-container/sagas/toast';
import routeConfig from './routes/config';
import { AppState } from './reducers';
import { UserSettings } from './areas/user/types';
import * as S from './appStyles';

type Error = {
  message: string;
  stack: string;
};

const Error500 = () => <ErrorMessage status={500} />;
const Error500WithMessage = (error: Error) => (
  <ErrorMessage message={error.message} stack={error.stack} status={500} />
);

const isRedirectedFromMarketRegex = /(&|^\?)redirect=(ie|it|pl)(&|$)/;

/**
 * A number so that we can track the render after the
 * initial mounting. This is when experiments are loaded and viewportcontext is updated.
 * This is used to add user performance timings.
 */
let renders = 0;

type Props = {
  infoPages: number[];
  campaigns: AppState['globals']['campaigns'];
  user?: UserSettings;
  layout?: string;
  isAuthenticated: boolean;
  renderToastContainer?: boolean;
  callback: () => void;
};

const App = ({
  layout = 'default',
  campaigns,
  user,
  isAuthenticated,
  renderToastContainer,
  callback,
}: Props) => {
  const location = useLocation();

  const {
    market: { routes, code, sourcePointCMP },
    renderMode,
  } = useContext(AppContext);

  renders += 1;
  if (renders === 2) {
    addPerformanceTiming('firstRerender');
  }

  const TRouter: any = TransitionRouter; // Typing as any until TransitionRouter can become Typescript - The 'old' withRouter has poor typing support
  const router = (
    <FetchContext.Consumer>
      {(fetchContext: any) => {
        return (
          <TRouter
            loaderAfter={1500}
            fetchContext={fetchContext}
            campaigns={campaigns}
            routes={routes}
            personalizationClientId={
              user && user.personalization && user.personalization.clientId
            }
            pulseEnvironmentId={
              user &&
              user.personalization &&
              user.personalization.pulseEnvironmentId
            }
            isAuthenticated={isAuthenticated}
            routeConfig={routeConfig}
          />
        );
      }}
    </FetchContext.Consumer>
  );

  const openNewsletterSignup = Object.keys(
    queryString.parse(location.search.substr(1))
  ).includes(NEWSLETTER_PARAM);

  const isRedirectedFromMarket = isRedirectedFromMarketRegex.test(
    location.search
  );
  const redirectedFromMarket =
    isRedirectedFromMarket &&
    (isRedirectedFromMarketRegex.exec(location.search) || [])
      .filter(match => /^(ie|it|pl)$/.test(match))
      .pop();

  const isWebViewLayout = renderMode === 'native';

  return (
    <ErrorBoundary onError={Error500}>
      <UIDReset>
        <S.App layout={layout} ref={callback}>
          {openNewsletterSignup && <NewsletterSignupModal />}

          <Viewport tablet mobile>
            <ExperimentEditor />
          </Viewport>

          <ViewportTracker>
            {!isWebViewLayout && <MainHeader />}
            {renderToastContainer && <ToastContainer />}
            <Viewport desktop tablet>
              <Feedback />
            </Viewport>
            <ErrorBoundary onError={noop}>
              {sourcePointCMP?.enabled ? (
                <CookieBannerSourcePoint />
              ) : (
                <>
                  <CoBrandingBanner />
                  <CookieBanner />
                </>
              )}
            </ErrorBoundary>
            <ErrorBoundary onError={noop}>
              <StaticIntercom />
            </ErrorBoundary>
            <ErrorBoundary onError={Error500WithMessage}>
              <S.Main data-test="AppMain" data-test-market-id={code}>
                {!isWebViewLayout && <Sidebar />}

                <S.Content>
                  {router}
                  {!isWebViewLayout && <Footer />}
                </S.Content>
              </S.Main>
            </ErrorBoundary>

            <TocModal />

            {isRedirectedFromMarket && (
              <ShutdownMessage redirectedFromMarket={redirectedFromMarket} />
            )}
          </ViewportTracker>
        </S.App>
      </UIDReset>
    </ErrorBoundary>
  );
};

const mapStateToProps = (state: AppState) => ({
  ...state.globals,
  user: state.areas.user,
  isAuthenticated: state.areas.account.isAuthenticated,
  renderToastContainer: state.toast.required,
});

export default compose(connect(mapStateToProps), memo)(App);
